import { useState, useRef, useEffect } from 'react';
import { useAblyChat } from '@/hooks/useAblyChat';
import { useAuthStore } from '@/store/authStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MessageCircle, Send, X, Minimize2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import type { ChatMessage } from '@/utils/ably';

interface OrderChatProps {
  orderId: string;
  isOpen?: boolean;
  onToggle?: (open: boolean) => void;
}

export function OrderChat({ orderId, isOpen: controlledOpen, onToggle }: OrderChatProps) {
  const { user, profile } = useAuthStore();
  const { messages, isConnected, sendMessage } = useAblyChat(orderId);
  const [inputText, setInputText] = useState('');
  const [isOpen, setIsOpen] = useState(controlledOpen ?? false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const effectiveOpen = controlledOpen !== undefined ? controlledOpen : isOpen;

  const handleToggle = () => {
    const next = !effectiveOpen;
    if (controlledOpen === undefined) setIsOpen(next);
    onToggle?.(next);
  };

  // Auto-scroll to bottom
  useEffect(() => {
    if (effectiveOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, effectiveOpen]);

  // Focus input when opened
  useEffect(() => {
    if (effectiveOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [effectiveOpen]);

  const handleSend = () => {
    if (!inputText.trim() || !user?.id || !profile) return;
    const role = profile.role as 'customer' | 'staff' | 'admin' | 'manager';
    sendMessage(inputText.trim(), user.id, role, profile.full_name || user.email || 'User');
    setInputText('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const isMyMessage = (msg: ChatMessage) => msg.senderId === user?.id;

  return (
    <div className="fixed bottom-20 right-4 z-[60] flex flex-col items-end lg:bottom-4">
      {/* Chat Window */}
      <AnimatePresence>
        {effectiveOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="mb-3 w-[90vw] max-w-[380px] h-[500px] bg-white rounded-2xl shadow-2xl border border-gray-100 flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="px-4 py-3 bg-primary text-white flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                <div>
                  <h3 className="font-bold text-sm">Order Chat</h3>
                  <p className="text-[10px] text-white/80">
                    {isConnected ? 'Connected' : 'Connecting...'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleToggle}
                className="p-1 hover:bg-white/10 rounded-lg transition-colors"
              >
                <Minimize2 className="h-4 w-4" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-50">
              {messages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-gray-400">
                  <MessageCircle className="h-10 w-10 mb-2 opacity-30" />
                  <p className="text-sm">No messages yet</p>
                  <p className="text-xs">Start a conversation about your order</p>
                </div>
              ) : (
                messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${isMyMessage(msg) ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${
                        isMyMessage(msg)
                          ? 'bg-primary text-white rounded-br-md'
                          : 'bg-white text-gray-800 rounded-bl-md shadow-sm border border-gray-100'
                      }`}
                    >
                      {!isMyMessage(msg) && (
                        <p className="text-[10px] font-bold text-primary mb-0.5">
                          {msg.senderName}
                        </p>
                      )}
                      <p className="leading-relaxed">{msg.text}</p>
                      <p
                        className={`text-[10px] mt-1 ${
                          isMyMessage(msg) ? 'text-white/70' : 'text-gray-400'
                        }`}
                      >
                        {formatTime(msg.timestamp)}
                      </p>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="px-3 py-2 bg-white border-t border-gray-100 flex items-center gap-2 shrink-0">
              <Input
                ref={inputRef}
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type a message..."
                className="flex-1 h-10 rounded-full border-gray-200 bg-gray-50 text-sm"
              />
              <Button
                size="icon"
                onClick={handleSend}
                disabled={!inputText.trim()}
                className="h-10 w-10 rounded-full shrink-0"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toggle Button */}
      <motion.button
        whileTap={{ scale: 0.95 }}
        onClick={handleToggle}
        className={`h-14 w-14 rounded-full shadow-lg flex items-center justify-center transition-colors ${
          effectiveOpen ? 'bg-gray-800 text-white' : 'bg-primary text-white'
        }`}
      >
        {effectiveOpen ? (
          <X className="h-6 w-6" />
        ) : (
          <MessageCircle className="h-6 w-6" />
        )}
        {messages.length > 0 && !effectiveOpen && (
          <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 rounded-full text-[10px] text-white font-bold flex items-center justify-center">
            {messages.length > 9 ? '9+' : messages.length}
          </span>
        )}
      </motion.button>
    </div>
  );
}
